import React, { useState } from 'react'

import ApplyToAllHeader from '../BulkTable/ApplyToAllHeader'
import BulkTable, { BulkTableColumn } from '../BulkTable'
import { useBulkTableStyles } from '../BulkTable/styles'
import { FieldBooleanSelect, PeriodTypeSelect } from './CommissionSelects'
import { CommissionField, CommissionFieldValue } from './types'

export interface StoreCommissionValue {
  directCommission: boolean | null
  distribute: boolean | null
  periodType: 'single' | 'multiple' | null
  splitCommission: boolean | null
  storeId: string
  storeName: string
  storeSold: boolean | null
}

interface BulkCommissionTableProps {
  values: StoreCommissionValue[]
  onChange: (storeId: string, field: keyof Omit<StoreCommissionValue, 'storeId' | 'storeName'>, value: boolean | 'single' | 'multiple' | null) => void
  onApplyToAll: (field: keyof Omit<StoreCommissionValue, 'storeId' | 'storeName'>, value: boolean | 'single' | 'multiple' | null) => void
}

const applyToAllLabels: Record<CommissionField, string> = {
  directCommission: 'Aplicar remuneração para todas as lojas',
  distribute: 'Aplicar distribuição da meta para todas as lojas',
  periodType: 'Aplicar período da meta para todas as lojas',
  splitCommission: 'Aplicar base da comissão para todas as lojas',
  storeSold: 'Aplicar modelo da meta para todas as lojas'
}

const BulkCommissionTable: React.FC<BulkCommissionTableProps> = ({ values, onChange, onApplyToAll }) => {
  const classes = useBulkTableStyles()
  const [applyValues, setApplyValues] = useState<Record<CommissionField, CommissionFieldValue>>({
    directCommission: null,
    distribute: null,
    periodType: null,
    splitCommission: null,
    storeSold: null
  })

  const splitCommissionHeaderDisabled = values.length > 0 && values.every(value => value.periodType === 'single')

  const renderHeaderSelect = (field: CommissionField, value: CommissionFieldValue) => {
    if (field === 'periodType') {
      return (
        <PeriodTypeSelect
          className={classes.headerSelect}
          value={value as 'single' | 'multiple' | null}
          onChange={nextValue => setApplyValues(current => ({ ...current, [field]: nextValue }))}
        />
      )
    }
    return (
      <FieldBooleanSelect
        className={classes.headerSelect}
        disabled={field === 'splitCommission' && splitCommissionHeaderDisabled}
        field={field}
        value={value as boolean | null}
        onChange={nextValue => setApplyValues(current => ({ ...current, [field]: nextValue }))}
      />
    )
  }

  const buildHeaderControl = (field: CommissionField) => {
    const value = applyValues[field]
    const disabled = value === null || (field === 'splitCommission' && splitCommissionHeaderDisabled)
    return (
      <ApplyToAllHeader ariaLabel={applyToAllLabels[field]} disabled={disabled} onApply={() => onApplyToAll(field, value)}>
        {renderHeaderSelect(field, value)}
      </ApplyToAllHeader>
    )
  }

  const columns: BulkTableColumn<StoreCommissionValue>[] = [
    {
      headerLabel: 'Remuneração',
      headerControl: buildHeaderControl('directCommission'),
      renderCell: row => <FieldBooleanSelect field='directCommission' value={row.directCommission} onChange={next => onChange(row.storeId, 'directCommission', next)} />
    },
    {
      headerLabel: 'Período da meta',
      headerControl: buildHeaderControl('periodType'),
      renderCell: row => <PeriodTypeSelect value={row.periodType} onChange={next => onChange(row.storeId, 'periodType', next)} />
    },
    {
      headerLabel: 'Base da comissão',
      headerControl: buildHeaderControl('splitCommission'),
      renderCell: row => <FieldBooleanSelect field='splitCommission' value={row.splitCommission} disabled={row.periodType === 'single'} onChange={next => onChange(row.storeId, 'splitCommission', next)} />
    },
    {
      headerLabel: 'Distribuição da meta',
      headerControl: buildHeaderControl('distribute'),
      renderCell: row => <FieldBooleanSelect field='distribute' value={row.distribute} onChange={next => onChange(row.storeId, 'distribute', next)} />
    },
    {
      headerLabel: 'Modelo da meta',
      headerControl: buildHeaderControl('storeSold'),
      renderCell: row => <FieldBooleanSelect field='storeSold' value={row.storeSold} onChange={next => onChange(row.storeId, 'storeSold', next)} />
    }
  ]

  return <BulkTable columns={columns} rows={values} />
}

export default BulkCommissionTable
